// Realtime AI sufficiency check on intake answers (currently only the
// PA-initial chief complaint). Pure helpers so the gating logic is unit-testable
// without the questionnaire component. Server contract + rollout modes:
// docs/pa-chief-complaint-realtime-check.md.
import { CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY } from "~/shared/sharedConstants.js";
import KickClient from "./api.js";

// Mirrors validators.AnswerCheckMaxAttempts on the backend: past this many
// checks on one question the client stops calling and lets the patient
// through — the third edited attempt advances with no validation at all.
export const AI_CHECK_MAX_ATTEMPTS = 2;

// Fixed retry messages shown on an insufficient verdict — the AI validates but
// never drafts patient-facing text. Index 0 after the first failed check,
// index 1 after the second (and last) one. Paragraph breaks are real \n\n;
// the callout renders them with pre-line whitespace.
export const ANSWER_CHECK_RETRY_MESSAGES = [
  "Can you explain a bit more specifically what kinds of problems you are " +
    "experiencing and when you experience them?\n\n" +
    "Are your symptoms related to a specific situation like public speaking, " +
    "or is it more generalized?",
  "We'd still like a bit more detail so your doctor can evaluate you " +
    "thoroughly.\n\n" +
    "For example: what symptoms do you notice, when do they tend to come up " +
    "(like presentations, meetings, or social situations), and how do they " +
    "affect you?",
];

// questionNeedsAiCheck gates the whole feature client-side: the question must
// opt in via its definition AND the consultation must be PA initial. GA renders
// the same questionnaire file (and therefore carries the same flag) but is
// deliberately excluded — the server enforces the same allowlist.
export function questionNeedsAiCheck(question, consultationType) {
  return (
    question?.aiSufficiencyCheck === true &&
    consultationType === CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY
  );
}

// assessAnswer POSTs the raw answer for assessment. The 10s per-request timeout
// overrides KickClient's 60s default and sits just above the server's own 9s
// deadline — a slow LLM turns into a catch (fail open), not a stuck spinner.
export function assessAnswer({
  consultationType,
  questionCode,
  answerText,
  attempt,
}) {
  return KickClient.newAuthenticatedClient()
    .post(
      "/visits/assess-answer",
      {
        consultation_type: consultationType,
        question_code: questionCode,
        answer_text: answerText,
        attempt,
      },
      { timeout: 10000 },
    )
    .then((response) => response.data);
}

// resolveAssessOutcome turns a server response into the gating decision. The
// client blocks ONLY on an explicit, enforce-mode, LLM-checked insufficient
// verdict — anything else (off/shadow mode, unchecked/fail-open response,
// missing response) proceeds. On a block the message shown is fixed copy
// picked by attempt number, never server text.
export function resolveAssessOutcome({ response, attempts }) {
  const proceed =
    !response ||
    response.mode !== "enforce" ||
    !response.checked ||
    response.sufficient === true;

  if (proceed) {
    return { proceed: true, guidance: "" };
  }

  return {
    proceed: false,
    guidance:
      ANSWER_CHECK_RETRY_MESSAGES[
        Math.min(Math.max(attempts, 1), ANSWER_CHECK_RETRY_MESSAGES.length) - 1
      ],
  };
}

// classifyAssessError buckets an axios failure for telemetry (never for
// gating — every error path proceeds).
export function classifyAssessError(error) {
  if (error?.code === "ECONNABORTED") {
    return "timeout";
  }
  if (!error?.response) {
    return "network";
  }
  return error.response.status >= 500 ? "http_5xx" : "http_4xx";
}
